import * as React from 'react';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { fetchUserById } from 'Services/Api/_HumanWritten/UserService';
import { CheckboxInput } from 'Views/Components/New/CheckboxInput';
import { appConfigStore } from 'Models/AppConfigStore';
import { store } from 'Models/Store';

export interface MarketingSubscriptionCheckboxProps {
	/**
	 * Id of the user the booking is for.
	 */
	userId: string | undefined;
	/**
	 * Current acknowledgment value in the wizard data. `undefined` means "no value chosen
	 * yet" — we render the checkbox unchecked and seed the wizard with `false` so the
	 * field is always defined whenever the checkbox is visible.
	 */
	value: boolean | undefined;
	onChange: (value: boolean | undefined) => void;
}

export function MarketingSubscriptionCheckbox({ userId, value, onChange }: MarketingSubscriptionCheckboxProps) {
	const intl = useIntl();
	const marketingEnabled = appConfigStore.marketingEnabled;
	const isSelfBooking = !!userId && userId === store.userData?.id;

	const { data: fetchedBookingUser } = useQuery({
		queryKey: ['booking-user-marketing-state', userId],
		queryFn: () => fetchUserById(userId!),
		// Only fetch when the booked user isn't already in the store (i.e. staff on behalf)
		// and the feature gate is on. Saves the round trip on the common self-booking path.
		enabled: marketingEnabled && !!userId && !isSelfBooking,
		// Stale time covers a single wizard session — the staleness only affects whether to
		// *show* the checkbox; the server still owns the canonical answer on submit.
		staleTime: 5 * 60 * 1000,
	});

	// Whichever side holds the current subscription state.
	const subscriptionState = isSelfBooking
		? store.userData?.isSubscribedToMarketing
		: fetchedBookingUser?.isSubscribedToMarketing;

	// Guest accounts are created transiently during checkout and may be reused across
	// future bookings — we always want to re-ask for marketing consent rather than honour
	// a stored answer from a previous flow. Matches the server's acknowledge endpoint,
	// which overwrites unconditionally for guests.
	const isGuest = isSelfBooking ? store.userData?.isGuest : fetchedBookingUser?.isGuest;

	// Staff-on-behalf path is still waiting on the fetch — don't render or seed yet.
	const isLoadingBookingUser = marketingEnabled && !!userId && !isSelfBooking && !fetchedBookingUser;

	// "Has acknowledged on the account" is the canonical not-null check — but guests
	// bypass it entirely so the checkbox always shows for them.
	const acknowledgedOnAccount = !isGuest && (subscriptionState === true || subscriptionState === false);

	// Reconcile wizardData with the account: pre-tick the checkbox when we're about to
	// show it (default is "subscribe" — they can untick before continuing) and clear
	// any stale value when the account already has a decision, so a previous booking's
	// value doesn't get re-sent for a customer who shouldn't see the checkbox at all.
	useEffect(() => {
		if (!marketingEnabled) return;
		if (!userId) return;
		if (isLoadingBookingUser) return;
		if (acknowledgedOnAccount) {
			if (value !== undefined) onChange(undefined);
			return;
		}
		if (value === undefined) onChange(true);
	}, [marketingEnabled, userId, isLoadingBookingUser, acknowledgedOnAccount, value, onChange]);

	if (!marketingEnabled || !userId || isLoadingBookingUser || acknowledgedOnAccount) {
		return null;
	}

	return (
		<div className="marketing-subscription-checkbox">
			<CheckboxInput
				label={intl.formatMessage({
					id: 'booking__marketing-subscription__label',
					defaultMessage: 'Subscribe to marketing communications',
				})}
				description={intl.formatMessage({
					id: 'booking__marketing-subscription__description',
					defaultMessage: 'Receive promotional offers, schedule changes, and updates from us.',
				})}
				checked={value === true}
				onChange={e => onChange(e.target.checked)}
			/>
		</div>
	);
}
